
import { useCallback, useEffect, useReducer, useState } from "react";
import { Mui } from "@osu/react-ui";
import Questions from "../../../Common/components/Questions.js";
import { advisorApprovalQuestions, gradFacultyRepApprovalQuestions, gradFacultyRepApprovalSummary } from "../../../Forms/data/ReportOnFinalExam.js";
import { ACTION_STATUS, APPROVAL_STATUS, ERROR_MESSAGE, EVENT_APPROVE_APPROVAL_FORM, EVENT_SAVE_APPROVAL_FORM, WorkflowApprovalTask } from "../../../util/constants.js";

export default function ReportOnFinalExam(props = {}) {
    const { approve, approvalTask, form, formActionStatus, initialized, save, setInitialized } = props;
    const [questionValues, questionDispatch] = useReducer(questionReducer, {});
    const [questions, setQuestions] = useState([]);
    const [Summary, setSummary] = useState(null);
    const [wrapperProps, setWrapperProps] = useState({});

    const buildApprovalForm = (q, qv) => {
        const approvalForm = {};
        for(const question of q) {
            const questionValue = qv[question.id].value;
            const value = (questionValue?.value ?? questionValue);
            const dataFields = question.dataField.split(".");
            if(dataFields.length > 1) {
                const formField = dataFields.pop();
                for(let i = 0; i < dataFields.length; i++) {
                    const nestedFormField = dataFields[i];
                    if(!approvalForm[nestedFormField]) approvalForm[nestedFormField] = {};
                    if(i === (dataFields.length - 1)) {
                        approvalForm[nestedFormField] = { ...approvalForm[nestedFormField], [formField]: value };
                    }
                }
            } else {
                const formField = dataFields[0];
                approvalForm[formField] = value;
            }
        }
        return approvalForm;
    };
    const buildQuestionValues = useCallback((questions) => {
        return questions.reduce((acc, question) => {
            const field = question.dataField;
            const fields = field.split(".");
            let value = null;
            if(fields.length > 1) {
                for(const f of fields) {
                    value = (value === null ? form[f] : value[f]);
                }
            } else {
                value = form[field];
            }
            acc[question.id] = { value };
            return acc;
        }, {});
    }, [form]);
    const validate = (f, q, qv) => {
        let isValid = true;
        for(const question of q) {
            const questionValue = qv[question.id];
            let error = null;
            if(question.validation) {
                error = question.validation(f, questionValue.value);
                if(error) isValid = false;
            } else if(question.required === true) {
                if(!questionValue?.value) error = ERROR_MESSAGE.REQUIRED;
                if(error) isValid = false;
            }
            questionDispatch({ type: "UPDATE", payload: { error, field: question.id, value: questionValue.value } });
        }
        return isValid;
    };
    const onSave = useCallback(() => {
        const approvalForm = buildApprovalForm(questions, questionValues);
        save(approvalForm);
    }, [save, questions, questionValues]);
    const onApprove = useCallback(() => {
        const isValid = validate(form, questions, questionValues);
        if(isValid) {
            const approvalForm = buildApprovalForm(questions, questionValues);
            approve(approvalForm);
        }
    }, [approve, form, questions, questionValues]);

    // initialize approval form (if necessary)
    useEffect(() => {
        if(initialized === false && form && form.status === APPROVAL_STATUS.PENDING) {
            let approvalFormQuestions = null;
            let approvalFormSummary = null;
            let approvalFormWrapperProps = null;
            // Advisor Approval
            if(approvalTask === WorkflowApprovalTask.ADVISOR_APPROVAL) {
                approvalFormQuestions = advisorApprovalQuestions;
                approvalFormWrapperProps = { maxWidth: "35rem" };
            }
            // Grad Faculty Rep Approval
            if(approvalTask === WorkflowApprovalTask.GRAD_FACULTY_REP_APPROVAL) {
                approvalFormQuestions = gradFacultyRepApprovalQuestions;
                approvalFormSummary = gradFacultyRepApprovalSummary;
            }
            if(approvalFormQuestions) {
                setQuestions(approvalFormQuestions);
                questionDispatch({ type: "INITIALIZE", payload: buildQuestionValues(approvalFormQuestions) });
            }
            if(approvalFormSummary) setSummary(approvalFormSummary);
            if(approvalFormWrapperProps) setWrapperProps(approvalFormWrapperProps);
        }
    }, [approvalTask, buildQuestionValues, form, initialized]);

    // add listener for save approval form event
    useEffect(() => {
        document.addEventListener(EVENT_SAVE_APPROVAL_FORM, onSave);
        return () => {
            document.removeEventListener(EVENT_SAVE_APPROVAL_FORM, onSave);
        };
    }, [onSave]);

    // add listener for approve approval form event
    useEffect(() => {
        document.addEventListener(EVENT_APPROVE_APPROVAL_FORM, onApprove);
        return () => {
            document.removeEventListener(EVENT_APPROVE_APPROVAL_FORM, onApprove);
        };
    }, [onApprove]);

    if(questions.length > 0) {
        if(initialized === false) setInitialized(true);
        return (
            <Mui.Box display={(formActionStatus === ACTION_STATUS.LOADING ? "none" : "block")} paddingTop="1rem">
                <Mui.Typography component="h3" variant="h4" className="sr-only">Approval Form</Mui.Typography>
                <Mui.Box {...wrapperProps}>
                    {Summary}
                    <Questions questions={questions} dispatchStepState={questionDispatch} values={questionValues} />
                </Mui.Box>
            </Mui.Box>
        );
    }

    return null;
}

function questionReducer(state, action) {
    const { payload, type } = action;
    switch(type) {
        case "INITIALIZE":
            return payload;
        case "UPDATE": {
            const { error = null, field, value } = payload;
            const fieldState = state[field];
            return { ...state, [field]: { ...fieldState, error, value: (value?.value ?? value) } };
        }
        default:
            return state
    }
}